// Shared, framework-free helpers for the "Autogenerate from gwbk" UI — the run
// list, the setup dialog, and the job detail view all derive their state badges
// and summaries from here so the wording/colours stay in sync.

import type {
  GwbkAutogenJobSummary,
  GwbkAutogenState
} from '@axilon-apps/domains-ontology-ui-state'

/** Run states whose worker is still doing work (polling + "Stop" apply). */
export const ACTIVE_STATES: ReadonlySet<GwbkAutogenState> = new Set([
  'capturing',
  'planning',
  'building'
])

/** True while a run is still capturing, planning, or building. */
export function isActiveState(state: GwbkAutogenState | undefined): boolean {
  return state !== undefined && ACTIVE_STATES.has(state)
}

export interface StatusMeta {
  label: string
  className: string
  spin?: boolean
}

/** Badge label + classes (and whether to spin) for a run state. */
export function statusMeta(state: GwbkAutogenState): StatusMeta {
  switch (state) {
    case 'capturing':
      return { label: 'Capturing', className: 'bg-brand/15 text-brand', spin: true }
    case 'planning':
      return { label: 'Planning', className: 'bg-brand/15 text-brand', spin: true }
    case 'building':
      return { label: 'Building', className: 'bg-brand/15 text-brand', spin: true }
    case 'done':
      return {
        label: 'Done',
        className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
      }
    case 'cancelled':
      return { label: 'Cancelled', className: 'bg-bg-sunk text-ink-3' }
    case 'error':
      return { label: 'Failed', className: 'bg-destructive/15 text-destructive' }
    default:
      return { label: state, className: 'bg-bg-sunk text-ink-3' }
  }
}

/** Short local date/time, or '' when the value can't be parsed. */
export function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

/** Compact one-line summary of what a run produced. */
export function summaryLine(job: GwbkAutogenJobSummary): string {
  const parts: string[] = []
  parts.push(`${job.completed}/${job.total} captured`)
  if (job.plannedCount > 0) parts.push(`${job.plannedCount} planned`)
  if (job.builtCount > 0 || job.buildFailedCount > 0) {
    parts.push(
      `${job.builtCount} built${job.buildFailedCount > 0 ? `, ${job.buildFailedCount} failed` : ''}`
    )
  }
  return parts.join(' · ')
}
